import React, { useCallback, useEffect, useRef } from 'react';
import { useStoreState } from 'easy-peasy';
import DynamicTable from "../../../../widgets/dynamicTable/DynamicTable";
import HelperMetiers360 from "../../../../../services/HelpersMetiers360.js";
import PropTypes from 'prop-types';
import { Alert, Button, Form } from 'react-bootstrap';
import Loader from '../../../../widgets/Loader';
import AuthorizationChecker from '../../../../../services/AuthorizationChecker.js';
import axios from '../../../../../services/axios';
import { useToast } from '../../../../../hooks/useToast.jsx';
import LogsComponent from '../../../../logs/LogsComponent.jsx';
import Webapp2UsersTable from './Webapp2UsersTable.jsx';

const GROUP_CODE_REGEX = /^[A-Z0-9]{3,10}$/;
const GROUP_CODE_ERROR = "Le code de groupe doit contenir entre 3 et 10 caractères (uniquement des lettres et des chiffres).";

const Webapp2GroupForm = ({ group, csrfToken }) => {
    const handleCodeChange = (e) => {
        e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    };

    return (
        <Form id="webapp2GroupForm" onSubmit={(event) => event.preventDefault()}>
            <p className="text-muted">
                Le code de groupe est automatiquement en majuscule, il doit être unique et
                comporter entre 3 et 10 caractères (uniquement des lettres et des chiffres).
            </p>
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Nom de groupe</Form.Label>
                <Form.Control
                    name="name"
                    defaultValue={group?.name ?? ''}
                    placeholder='Nom du groupe'
                />
                <Form.Text className="text-muted">
                    Exemple : Groupe 1 - semaine paire
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Code de groupe</Form.Label>
                <Form.Control
                    name="code"
                    defaultValue={group?.code ?? ''}
                    placeholder='Code du groupe'
                    onChange={handleCodeChange}
                    maxLength={10}
                    minLength={3}
                />
                <Form.Text className="text-muted">
                    Exemple : G1SP
                </Form.Text>
            </Form.Group>
            <Form.Control name="csrf_token" defaultValue={csrfToken} type="hidden" />
        </Form>
    );
};


const Webapp2Groups = ({ groups, setGroups, userStats, setUserStats, setModalData, modalData, setModalShow, setIsSending, clientById, onCreateReady, readOnly = false }) => {

    const { apiData } = useStoreState(state => state.actionSlugs);
    const toast = useToast();
    const modalDataRef = useRef(modalData);
    useEffect(() => { modalDataRef.current = modalData; }, [modalData]);

    const validateGroupCode = (formData) => {
        if (GROUP_CODE_REGEX.test(formData.get('code'))) return true;
        toast.open({ message: GROUP_CODE_ERROR, variant: 'danger' });
        return false;
    };

    const toggleUserGroup = (user, group, action, allUsers) => {
        setIsSending(true);
        const updateUrl = apiData.ACTIONS?.webapp2User?.update?.url?.replace('uniqueId', user.uniqueId);
        const csrfToken = apiData.ACTIONS?.webapp2User?.update?.csrfToken;
        const formData = new FormData();
        formData.set('csrf_token', csrfToken);
        formData.set('webapp2GroupsUpdated', '1');

        const currentGroupIds = user.webapp2Groups?.map(g => g.uniqueId) ?? [];
        let newGroupIds;
        if (action === 'add') {
            newGroupIds = [...currentGroupIds, group.uniqueId];
        } else {
            newGroupIds = currentGroupIds.filter(id => id !== group.uniqueId);
        }
        newGroupIds.forEach(id => formData.append('webapp2Groups[]', id));

        return axios.post(updateUrl, formData)
            .then((response) => {
                if (setUserStats) {
                    setUserStats(prev => prev?.map(u => u.uniqueId === user.uniqueId
                        ? { ...u, ...response }
                        : u
                    ));
                }
                const updatedStats = (allUsers ?? userStats ?? []).map(u =>
                    u.uniqueId === user.uniqueId ? { ...u, ...response } : u
                );
                setGroups(prev => prev.map(g => {
                    if (g.uniqueId !== group.uniqueId) return g;
                    const count = updatedStats.filter(u =>
                        u.webapp2Groups?.some(ug => ug.uniqueId === g.uniqueId)
                    ).length;
                    return { ...g, webapp2UsersCount: count };
                }));
                toast.success();
                return response;
            })
            .catch((error) => {
                toast.error();
                return Promise.reject(error);
            })
            .finally(() => setIsSending(false));
    };

    const openEditModal = (group) => {
        const validate = () => {
            const formData = new FormData(document.getElementById('webapp2GroupForm'));
            if (!validateGroupCode(formData)) return;

            setIsSending(true);
            const updateUrl = apiData.ACTIONS?.webapp2Group?.update?.url?.replace('uniqueId', group.uniqueId);

            return axios.post(updateUrl, formData)
                .then((response) => {
                    setGroups(prev => prev.map(g => g.uniqueId === group.uniqueId
                        ? { ...g, ...response }
                        : g
                    ));
                    if (setUserStats) {
                        setUserStats(prev => prev?.map(u => ({
                            ...u,
                            webapp2Groups: u.webapp2Groups?.map(g => g.uniqueId === group.uniqueId
                                ? { ...g, ...response }
                                : g
                            )
                        })));
                    }
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        const csrfToken = apiData.ACTIONS?.webapp2Group?.update?.csrfToken;

        setModalData({
            ...modalData,
            header: <>Édition du groupe : {group.name}</>,
            content: <Webapp2GroupForm group={group} csrfToken={csrfToken} />,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        });
        setModalShow(true);
    };

    const openGroupDetailModal = (uniqueId, allUsers = userStats ?? []) => {
        const group = groups?.find(g => g.uniqueId === uniqueId);
        if (!group) return;

        const groupUsers = allUsers.filter(u =>
            u.webapp2Groups?.some(g => g.uniqueId === uniqueId)
        );

        setModalData({
            ...modalData,
            header: <>
                {group.name}
                <div className="mt-1"><small className="fw-normal">Code groupe webapp : <b>{group.code}</b></small></div>
            </>,
            content: <Webapp2UsersTable users={groupUsers} groups={groups}
                    valueInitSort="userName" tableName="webapp2GroupUsers" filename={`Webapp-groupe-utilisateurices-${HelperMetiers360.getdisplayDateType(new Date(), 'day')}`}
                    noResultMessage="Aucun utilisateur dans ce groupe." />,
            size: 'xl'
        });
        setModalShow(true);
    };

    const openAllUsersModal = (group, allUsers = userStats ?? []) => {
        const renderActionButton = (user) => {
            const isInGroup = user.webapp2Groups?.some(g => g.uniqueId === group.uniqueId);
            return (
                <Button
                    variant={isInGroup ? "danger" : "success"}
                    className="p-1 px-2 m-1" size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleUserGroup(user, group, isInGroup ? 'remove' : 'add', allUsers).then((response) => {
                            const updatedUsers = allUsers.map(u =>
                                u.uniqueId === user.uniqueId ? { ...u, ...response } : u
                            );
                            openAllUsersModal(group, updatedUsers);
                        });
                    }}>
                    {isInGroup ? 'Retirer' : 'Affecter'}
                </Button>
            );
        };

        setModalData({
            ...modalData,
            header: <>Affecter / retirer des utilisateur·rices au groupe : <b>{group.name}</b></>,
            content: allUsers.length > 0
                ? <Webapp2UsersTable users={allUsers} groups={groups} actionButton={renderActionButton}
                    valueInitSort="userName" tableName="webapp2GroupUsers" filename={`Webapp-groupe-utilisateurices-${HelperMetiers360.getdisplayDateType(new Date(), 'day')}`}
                    noResultMessage="Aucun utilisateur dans ce groupe." />
                : <Alert variant='warning'>Aucun utilisateur disponible.</Alert>,
            size: 'xl'
        });
        setModalShow(true);
    };

    const openDeleteModal = (group) => {
        setModalData({
            ...modalData,
            header: <>Suppression du groupe</>,
            content: <h5>Êtes-vous sûr·e de vouloir supprimer le groupe <b>{group.name}</b> ?</h5>,
            resetButton: 'Supprimer',
            cancelButton: 'Annuler',
            onReset: () => {
                setIsSending(true);
                const deleteUrl = apiData.ACTIONS?.webapp2Group?.delete?.url?.replace('uniqueId', group.uniqueId);
                const csrfToken = apiData.ACTIONS?.webapp2Group?.delete?.csrfToken;
                const formData = new FormData();
                formData.set('csrf_token', csrfToken);

                axios.post(deleteUrl, formData)
                    .then(() => {
                        setGroups(prev => prev.filter(g => g.uniqueId !== group.uniqueId));
                        if (setUserStats) {
                            setUserStats(prev => prev?.map(u => ({
                                ...u,
                                webapp2Groups: u.webapp2Groups?.filter(g => g.uniqueId !== group.uniqueId)
                            })));
                        }
                        toast.success();
                        setModalShow(false);
                    })
                    .catch((error) => Promise.reject(error))
                    .finally(() => setIsSending(false));
            },
        });
        setModalShow(true);
    };

    const openCreateModal = useCallback(() => {
        const validate = () => {
            const formData = new FormData(document.getElementById('webapp2GroupForm'));
            if (!validateGroupCode(formData)) return;

            setIsSending(true);
            const createUrl = apiData.ACTIONS?.webapp2Group?.create?.url
                ?.replace('uniqueId', clientById?.uniqueId ?? clientById);

            return axios.post(createUrl, formData)
                .then((response) => {
                    setGroups(prev => [...prev, response]);
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        const csrfToken = apiData.ACTIONS?.webapp2Group?.create?.csrfToken;

        setModalData({
            ...modalDataRef.current,
            header: <>Nouveau groupe webapp</>,
            content: <Webapp2GroupForm csrfToken={csrfToken} />,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        });
        setModalShow(true);
    }, [apiData, clientById, setGroups, setIsSending, setModalData, setModalShow]);

    const sortType = groups?.length > 0
        && [
            {
                value: 'name', label: 'Nom de groupe',
                test: HelperMetiers360.isArrayContainsValue(groups, "name"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
                display: (group) => group.name ?? '-'
            },
            {
                value: 'code', label: 'Code de groupe',
                test: HelperMetiers360.isArrayContainsValue(groups, "code"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.code, b.code),
                display: (group) => group.code ?? '-'
            },
            {
                value: 'webapp2UsersCount', label: "Nombre d'utilisateur·rices",
                test: groups?.length > 0,
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.webapp2UsersCount, b.webapp2UsersCount),
                display: (group) => group.webapp2UsersCount
            },
            {
                value: 'users', label: '',
                test: !readOnly,
                display: (group) => <div className="d-flex gap-1 justify-content-center">
                    <Button variant="secondary" size="sm" className="m-0 p-2"
                        onClick={(e) => { e.stopPropagation(); openEditModal(group); }}>
                        <i className="fas fa-edit"></i>&nbsp;Infos
                    </Button>
                    <Button variant="secondary" size="sm" className="m-0 p-2"
                        onClick={(e) => { e.stopPropagation(); openAllUsersModal(group); }}>
                        <i className="fas fa-users"></i>&nbsp;Utilisateur·rices
                    </Button>
                </div>,
                flatDisplay: () => ''
            },
            {
                value: 'delete', label: 'Supprimer',
                test: !readOnly && AuthorizationChecker.hasDeleteRights('webapp2Group'),
                display: (group) => <Button variant="danger" size="sm" className="m-0 p-2"
                    onClick={(e) => { e.stopPropagation(); openDeleteModal(group); }}>
                    <i className="fas fa-trash-alt"></i>
                </Button>,
                flatDisplay: () => ''
            },
            {
                value: 'actionLogs', label: 'Logs',
                test: AuthorizationChecker.isAdmin(),
                display: (group) => <LogsComponent entityNameFromComponent="webapp2Group" uniqueIdFromComponent={group.uniqueId} />,
                flatDisplay: () => null
            }
        ];

    const handleClick = readOnly ? undefined : (uniqueId) => openGroupDetailModal(uniqueId);

    useEffect(() => {
        if (onCreateReady && apiData.ACTIONS?.webapp2Group?.create) {
            onCreateReady(openCreateModal);
        }
    }, [apiData, openCreateModal]);

    return !groups ?
        <Loader />
        : <>
            {/* ! A REMETTRE APRES DEV SESSION AUTHENTIFIEE */}
            {/* <Alert variant='info' className='mt-3'>
                Les "<b>codes de groupe</b>" permettent à vos utilisateur·rices de créer un compte webapp en étant automatiquement associé·e à un groupe. Cela peut aussi être fait après l'inscription via le menu "Profil" de la webapp.
            </Alert> */}
            <Alert variant='info' className='mt-3'>
                Les "<b>codes de groupe</b>" permettent à vos utilisateur·rices de se rattacher à un groupe depuis le menu "Profil" de la webapp. Plus tard, cela sera également possible lors de la création d'un compte webapp.
            </Alert>
            {groups?.length > 0 ?
                <DynamicTable
                    contentTable={groups}
                    contentSort={sortType}
                    handleClick={handleClick}
                    valueInitSort="name"
                    index='uniqueId'
                    tableName="webapp2Groups"
                    filename={`Webapp-groupes-${HelperMetiers360.getdisplayDateType(new Date(), 'day')}`}
                />
                : <Alert variant='warning'>Aucun groupe disponible.</Alert>
            }
        </>;
};

Webapp2Groups.propTypes = {
    groups: PropTypes.array,
    setGroups: PropTypes.func.isRequired,
    userStats: PropTypes.array,
    setUserStats: PropTypes.func,
    setModalData: PropTypes.func.isRequired,
    modalData: PropTypes.object.isRequired,
    setModalShow: PropTypes.func.isRequired,
    setIsSending: PropTypes.func.isRequired,
    clientById: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    onCreateReady: PropTypes.func,
    readOnly: PropTypes.bool,
};

export { Webapp2GroupForm };

export default Webapp2Groups;
